import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowRightIcon, LockKeyholeIcon, LoaderIcon, ZapIcon } from 'lucide-react';
const WAITLIST_URL = 'https://script.google.com/macros/s/AKfycbwh0J9Vkq6zT5v5-Pe-5GZczrP-7RSoI2ANR30Epc1TUMBa6CtWBK-Hu7W5Kn1EZVlOfw/exec';
export function HeroSection() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !email) return;
    setStatus('loading');
    try {
      await fetch(WAITLIST_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name,
          email
        })
      });
      setStatus('success');
      setName('');
      setEmail('');
      setTimeout(() => setStatus('idle'), 5000);
    } catch {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 4000);
    }
  };
  return <section className="relative pt-36 pb-16 sm:pt-36 sm:pb-20 lg:pt-44 lg:pb-28 overflow-hidden" data-id="element-87">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10" data-id="element-88">
        <div className="absolute -top-[20%] -right-[10%] w-[70%] h-[70%] rounded-full bg-kitty-yellow-100/40 blur-3xl" data-id="element-89" />
        <div className="absolute top-[40%] -left-[10%] w-[50%] h-[50%] rounded-full bg-blue-50/40 blur-3xl" data-id="element-90" />
      </div>

      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-8" data-id="element-91">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-10 items-center" data-id="element-92">
          {/* Left Column */}
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-xl" data-id="element-93">
            {/* Badge */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.1,
            duration: 0.4
          }} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-kitty-yellow-100 text-kitty-yellow-800 font-medium text-xs sm:text-sm mb-6 lg:mb-8" data-id="element-94">
              <span className="relative flex h-2 w-2" data-id="element-95">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-kitty-yellow-500 opacity-75" data-id="element-96" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-kitty-yellow-500" data-id="element-97" />
              </span>
              UK Early Access — Limited Spots
            </motion.div>

            {/* Headline */}
            <h1 className="text-[2rem] sm:text-[2.5rem] lg:text-5xl xl:text-6xl font-extrabold text-kitty-dark leading-[1.1] mb-6 lg:mb-6 tracking-tight" data-id="element-98">
              No more chasing friends for money
            </h1>

            {/* Subheading */}
            <p className="text-lg lg:text-xl font-medium text-gray-700 mb-4 leading-relaxed max-w-md" data-id="element-99">
              KittyPot helps groups track shared expenses, split costs fairly,
              and see the simplest way to settle up.
            </p>

            {/* Supporting copy */}
            <p className="text-base text-gray-500 mb-8 lg:mb-10 leading-relaxed max-w-md" data-id="element-100">
              Create a kitty for a trip, meal, house share, event or night out.
              Add expenses, choose who they're shared with, and let KittyPot
              work out who pays who.
            </p>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6" data-id="element-101">
              <button onClick={() => {
              document.getElementById('waitlist')?.scrollIntoView({
                behavior: 'smooth'
              });
            }} className="w-full sm:w-auto bg-kitty-yellow hover:bg-kitty-yellow-500 text-kitty-dark px-8 py-[16px] lg:py-4 min-h-[56px] rounded-full font-bold transition-all active:scale-[0.98] shadow-lg hover:shadow-xl flex items-center justify-center gap-2 text-base" data-id="element-102">
                Join the waitlist
              </button>
              <button onClick={() => {
              document.getElementById('how-it-works')?.scrollIntoView({
                behavior: 'smooth'
              });
            }} className="w-full sm:w-auto bg-white hover:bg-gray-50 text-kitty-dark px-8 py-[16px] lg:py-4 min-h-[56px] rounded-full font-bold transition-all active:scale-[0.98] border border-gray-200 shadow-sm flex items-center justify-center gap-2 text-base" data-id="element-103">
                See how it works
              </button>
            </div>

            {/* MVP Note */}
            <div className="flex items-center gap-3 mb-10 lg:mb-8 pl-1" data-id="element-104">
              <p className="text-xs text-gray-400 max-w-sm" data-id="element-105">
                Current MVP: expense tracking and manual settlement only.
                KittyPot does not hold or move money.
              </p>
            </div>
          </motion.div>

          {/* Right Column: App Mockup */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          rotate: 2
        }} animate={{
          opacity: 1,
          scale: 1,
          rotate: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2,
          type: 'spring'
        }} className="relative mx-auto w-full max-w-[240px] sm:max-w-[260px] lg:max-w-[320px] mt-8 lg:mt-0" data-id="element-106">
            {/* Floating +£50 badge */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.5,
            y: 10
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} transition={{
            delay: 1.6,
            duration: 0.4,
            type: 'spring',
            stiffness: 200
          }} className="absolute -top-3 -right-4 z-30 bg-green-500 text-white text-sm font-bold px-3.5 py-1.5 rounded-full shadow-lg" data-id="element-107">
              £50.00 split
            </motion.div>

            {/* Floating split notification */}
            <motion.div initial={{
            opacity: 0,
            y: 20,
            scale: 0.9
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} transition={{
            delay: 2.5,
            duration: 0.5,
            type: 'spring'
          }} className="absolute -bottom-4 -left-4 lg:-left-6 z-30 bg-kitty-dark text-white px-4 py-2.5 rounded-2xl shadow-xl max-w-[200px]" data-id="element-108">
              <p className="text-xs font-semibold" data-id="element-109">✓ Settled up</p>
              <p className="text-[10px] text-gray-400" data-id="element-110">
                4 people · everyone's square
              </p>
            </motion.div>

            <div className="relative bg-white rounded-[3rem] shadow-2xl border-[8px] border-gray-900 overflow-hidden aspect-[9/19]" data-id="element-111">
              <div className="absolute top-0 inset-x-0 h-6 flex justify-center z-20" data-id="element-112">
                <div className="w-32 h-6 bg-gray-900 rounded-b-3xl" data-id="element-113" />
              </div>
              <img src="https://cdn.magicpatterns.com/uploads/iFCdmKPA1edU6x7RzhLhxy/WhatsApp_Image_2026-06-24_at_00.08.47.jpg" alt="KittyPot app screenshot showing a Night Out kitty with £758.50 total spent and an Add Expense button" className="w-full h-full object-cover object-top" data-id="element-114" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}